<script setup>
import { ethers } from "ethers";

const props = defineProps({
  reward: Object,
  disperseToHolders: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["claimed", "claimFailed"]);
const claiming = ref(false);

const battleAirdropAddress = import.meta.env.VITE_CONTRACT_BATTLE_AIRDROP;

const prizeTitle = computed(() => {
  const count = props.reward.winCount || 0;
  const index = props.reward.binIndex;
  return `Battle Season #${index}, Win ${count} time${count > 1 ? "s" : ""}`;
});

const isClaimable = computed(() => !!props.reward.lockedAt);

const tokenAmount = computed(() => {
  try {
    const decimals = props.reward?.decimals ?? 18;
    return Number(ethers.formatUnits(props.reward?.rewardAmount ?? 0, decimals)) || 0;
  } catch (e) {
    console.error("formatUnits error", e);
    return 0;
  }
});

// Combine the shortened token amount with the token name
const tokenLabel = computed(
  () => `${formatNumber(tokenAmount.value)} ${props.reward.tokenName}`
);

const buttonText = computed(() => {
  return isClaimable.value
    ? `CLAIM! ${tokenLabel.value}`
    : `Estimated: ${tokenLabel.value}`;
});

const claimReward = async () => {
  if (claiming.value) return;
  claiming.value = true;
  try {
    console.log("🚀 Claiming reward:", props.reward);
    if (
      !props.reward?.proof?.length ||
      props.reward?.binIndex === undefined ||
      props.reward?.binIndex === null ||
      !props.reward?.winCount ||
      !props.reward?.rewardAmount
    ) {
      throw new Error("Invalid reward data");
    }

    const claimTokenAbi = getAbiByAddress(battleAirdropAddress);
    const iface = new ethers.Interface(claimTokenAbi);

    const calldata = iface.encodeFunctionData("claimWinsFromBinIndex", [
      props.reward.proof,
      props.reward.binIndex,
      props.reward.winCount.toString(),
      props.reward.rewardAmount,
      props.disperseToHolders,
    ]);

    const tx = {
      to: battleAirdropAddress,
      data: calldata,
      value: "0x0",
    };

    console.log("🚀 Claiming reward via Particle:", tx);
    const txHash = await ParticleBridge.sendTransaction(tx);

    emit("claimed", { reward: props.reward, txHash });
    return txHash;
  } catch (err) {
    console.error("❌ Error while claiming reward:", err);
    emit("claimFailed", err);
    throw err;
  } finally {
    claiming.value = false;
  }
};
</script>

<template>
  <v-list-item class="reward-item">
    <template v-slot:prepend>
      <v-avatar size="64">
        <v-img :src="reward.tokenIcon" />
      </v-avatar>
    </template>

    <div class="tw-mb-3">
      {{ prizeTitle }}
    </div>

    <v-btn
      color="white"
      variant="outlined"
      width="160"
      class="tw-border-2 !tw-rounded-xl btn-claim"
      :disabled="!isClaimable || claiming"
      @click="claimReward"
    >
      <div class="btn-overlay"></div>
      <div class="tw-z-[1] claim-action-text">
        {{ buttonText }}
      </div>
    </v-btn>
  </v-list-item>
</template>

<style lang="postcss">
.reward-item {
  .v-list-item__content {
    overflow: visible;
  }

  .claim-action-text {
    font-size: 12px !important;
    font-weight: 900;
    text-transform: uppercase;
    color: white;
    font-family: sans-serif;
    text-shadow:
      -1px -1px 0 #000,
      1px -1px 0 #000,
      -1px 1px 0 #000,
      1px 1px 0 #000,
      0px 2px 0 #000,
      -1px 2px 0 #000,
      1px 2px 0 #000;

    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Roboto", sans-serif;
  }

  .btn-claim {
    min-width: 160px;
    width: 160px;
    background: rgba(5, 248, 0, 0.76);

    box-shadow: 2px 2px 4px 0px rgba(0, 0, 0, 0.25);
    .btn-overlay {
      background: rgba(5, 248, 0, 0.76);
      box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
      filter: blur(10px);
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }
}
</style>
